import { useEffect } from 'react';

interface AutoUpstreamSyncArgs {
  data: any;
  sharedAddress: any;
  selectedProducts: any;
  getStepData: (id: string) => any;
  setData: (updater: (prev: any) => any) => void;
  saveStepData: (id: string, data: any) => void;
  screenId: string;
}

// Seeds data.BA/VP from the canonical shared address (and upstream products) so the calc can run
// even when the policyholder address was supplied on an earlier screen. AddressSection owns the
// editable inputs + lookup + shared-slice sync when it is shown. Address-only (no Gezinssamenstelling
// lock — Personenauto has none). Mirrors rechtsbijstand/useUpstreamSync; the [sharedAddress] dep is
// kept identical to preserve the original timing.
export function useUpstreamSync({
  data,
  sharedAddress,
  selectedProducts,
  getStepData,
  setData,
  saveStepData,
  screenId,
}: AutoUpstreamSyncArgs) {
  useEffect(() => {
    const ba = data?.Contractdocument?.PP?.BA;
    const vp = data?.Contractdocument?.PP?.VP;

    const upstreamBA =
      (selectedProducts?.woonhuis && getStepData('woonhuis')?.Contractdocument?.PP?.BA) ||
      (selectedProducts?.inboedel && getStepData('inboedel')?.Contractdocument?.PP?.BA) ||
      (selectedProducts?.aansprakelijkheid && getStepData('aansprakelijkheid')?.Contractdocument?.PP?.BA) ||
      (selectedProducts?.rechtsbijstand && getStepData('rechtsbijstand')?.Contractdocument?.PP?.BA) ||
      null;

    const bestPcode = sharedAddress?.BA_PCODE || ba?.BA_PCODE || vp?.VP_PCODE || upstreamBA?.BA_PCODE;
    const bestHuisnr = sharedAddress?.BA_HUISNR || ba?.BA_HUISNR || vp?.VP_HUISNR || upstreamBA?.BA_HUISNR;
    const bestToevoeg = sharedAddress?.BA_TOEVOEG ?? ba?.BA_TOEVOEG ?? vp?.VP_TOEVOEG ?? upstreamBA?.BA_TOEVOEG ?? '';
    const bestStraat = sharedAddress?.BA_STRAAT || ba?.BA_STRAAT || vp?.VP_STRAAT || upstreamBA?.BA_STRAAT;
    const bestPlaats = sharedAddress?.BA_PLAATS || ba?.BA_PLAATS || vp?.VP_PLAATS || upstreamBA?.BA_PLAATS;

    if (bestStraat && bestPlaats) {
      setData((prev: any) => {
        const prevBA = prev.Contractdocument?.PP?.BA || {};
        const prevVP = prev.Contractdocument?.PP?.VP || {};
        const updated = {
          ...prev,
          Contractdocument: {
            ...prev.Contractdocument,
            PP: {
              ...prev.Contractdocument?.PP,
              BA: {
                ...prevBA,
                BA_PCODE: bestPcode,
                BA_HUISNR: bestHuisnr,
                BA_TOEVOEG: bestToevoeg,
                BA_STRAAT: bestStraat,
                BA_PLAATS: bestPlaats,
              },
              VP: {
                ...prevVP,
                VP_PCODE: bestPcode || prevVP.VP_PCODE,
                VP_HUISNR: (bestHuisnr ? Number(bestHuisnr) : undefined) || prevVP.VP_HUISNR || 0,
                VP_TOEVOEG: bestToevoeg || prevVP.VP_TOEVOEG || '',
                VP_STRAAT: bestStraat || prevVP.VP_STRAAT,
                VP_PLAATS: bestPlaats || prevVP.VP_PLAATS,
              },
            },
          },
        };
        setTimeout(() => saveStepData(screenId, updated), 0);
        return updated;
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- sharedAddress is the trigger; the effect writes data.BA/VP itself
  }, [sharedAddress]);
}
